import type { WorkBook } from "xlsx";

export type CatalogKind = "courses" | "rooms" | "teachers" | "classes";

export interface CatalogImportRow {
  source_row: number;
  values: Record<string, unknown>;
}

export interface ParsedCatalogWorkbook {
  catalog_type: "COURSE" | "ROOM" | "TEACHER" | "CLASS";
  sheet_name: string;
  rows: CatalogImportRow[];
}

interface CatalogTemplate {
  catalogType: ParsedCatalogWorkbook["catalog_type"];
  filename: string;
  sheetName: string;
  headers: ReadonlyArray<{ label: string; field: string }>;
  examples: ReadonlyArray<ReadonlyArray<string | number>>;
  widths: number[];
}

export const CATALOG_TEMPLATES: Record<CatalogKind, CatalogTemplate> = {
  courses: {
    catalogType: "COURSE",
    filename: "课程信息导入模板.xlsx",
    sheetName: "课程信息",
    headers: [{ label: "课程名称", field: "name" }],
    examples: [["生活语文"], ["生活数学"]],
    widths: [26]
  },
  rooms: {
    catalogType: "ROOM",
    filename: "功能教室信息导入模板.xlsx",
    sheetName: "功能教室信息",
    headers: [
      { label: "教室名称", field: "name" },
      { label: "间数", field: "count" }
    ],
    examples: [["个训室", 6], ["言语训练室", 2]],
    widths: [26, 12]
  },
  teachers: {
    catalogType: "TEACHER",
    filename: "教师信息导入模板.xlsx",
    sheetName: "教师信息",
    headers: [
      { label: "教师姓名", field: "name" },
      { label: "课时数", field: "planned_lessons" },
      { label: "每天最大课时数", field: "max_daily_lessons" }
    ],
    examples: [["戴老师", 18, 5], ["刘老师", 20, 6]],
    widths: [26, 14, 20]
  },
  classes: {
    catalogType: "CLASS",
    filename: "班级信息导入模板.xlsx",
    sheetName: "班级信息",
    headers: [
      { label: "年级", field: "grade_label" },
      { label: "班级名称", field: "class_name" }
    ],
    examples: [["一年级", "1班"], ["一年级", "2班"]],
    widths: [18, 20]
  }
};

const TEMPLATE_NOTE = "下方示例会作为普通数据导入；不需要时请先删除示例行。";

export class CatalogWorkbookError extends Error {
  readonly sheetName?: string;
  readonly sourceRow?: number;
  readonly field?: string;

  constructor(
    message: string,
    location: { sheetName?: string; sourceRow?: number; field?: string } = {}
  ) {
    super(message);
    this.name = "CatalogWorkbookError";
    this.sheetName = location.sheetName;
    this.sourceRow = location.sourceRow;
    this.field = location.field;
  }
}

export async function downloadCatalogTemplate(kind: CatalogKind): Promise<void> {
  const XLSX = await import("xlsx");
  const template = CATALOG_TEMPLATES[kind];
  const sheet = XLSX.utils.aoa_to_sheet([
    [TEMPLATE_NOTE],
    template.headers.map((header) => header.label),
    ...template.examples.map((row) => [...row])
  ]);
  sheet["!cols"] = template.widths.map((wch) => ({ wch }));
  if (template.headers.length > 1) {
    sheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: template.headers.length - 1 } }
    ];
  }
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, template.sheetName);
  XLSX.writeFileXLSX(workbook, template.filename, { compression: true });
}

export async function parseCatalogWorkbook(
  file: File,
  kind: CatalogKind
): Promise<ParsedCatalogWorkbook> {
  const template = CATALOG_TEMPLATES[kind];
  if (!file.name.toLocaleLowerCase().endsWith(".xlsx")) {
    throw new CatalogWorkbookError("仅支持 .xlsx 文件，请使用本页面下载的模板");
  }

  let workbook: WorkBook;
  try {
    const XLSX = await import("xlsx");
    workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  } catch {
    throw new CatalogWorkbookError("无法读取该 Excel 文件，请确认文件没有损坏");
  }
  if (workbook.SheetNames.length !== 1) {
    throw new CatalogWorkbookError("文件必须且只能包含一个工作表");
  }
  const sheetName = workbook.SheetNames[0];
  const acceptedSheetNames =
    kind === "rooms" ? [template.sheetName, "教室信息"] : [template.sheetName];
  if (!acceptedSheetNames.includes(sheetName)) {
    throw new CatalogWorkbookError(
      `工作表名称应为“${template.sheetName}”，当前为“${sheetName}”`,
      { sheetName }
    );
  }
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) {
    throw new CatalogWorkbookError("工作表内容为空", { sheetName });
  }
  const XLSX = await import("xlsx");
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: true
  });
  const expectedHeaders = template.headers.map((header) => header.label);
  const legacyTeacherHeaders = ["教师姓名", "课时数"];
  const actualHeaders = (matrix[1] ?? []).map(cellText);
  const acceptedHeaders =
    actualHeaders.length === expectedHeaders.length &&
    expectedHeaders.every((header, index) => actualHeaders[index] === header)
      ? template.headers
      : kind === "teachers" &&
          actualHeaders.length === legacyTeacherHeaders.length &&
          legacyTeacherHeaders.every((header, index) => actualHeaders[index] === header)
        ? template.headers.slice(0, legacyTeacherHeaders.length)
        : null;
  if (!acceptedHeaders) {
    throw new CatalogWorkbookError(
      `第 2 行表头必须依次为：${expectedHeaders.join("、")}`,
      { sheetName, sourceRow: 2 }
    );
  }

  const dataRows = matrix.slice(2);
  while (dataRows.length && isBlankRow(dataRows[dataRows.length - 1] ?? [])) {
    dataRows.pop();
  }
  if (!dataRows.length) {
    throw new CatalogWorkbookError("工作表中没有可导入的数据", {
      sheetName,
      sourceRow: 3
    });
  }

  const rows = dataRows.map((row, index) => {
    const sourceRow = index + 3;
    const extraCell = row
      .slice(acceptedHeaders.length)
      .findIndex((value) => !isBlankCell(value));
    if (extraCell >= 0) {
      throw new CatalogWorkbookError("数据区域包含模板之外的列", {
        sheetName,
        sourceRow,
        field: `第 ${acceptedHeaders.length + extraCell + 1} 列`
      });
    }
    return {
      source_row: sourceRow,
      values: Object.fromEntries(
        acceptedHeaders.map((header, column) => [
          header.field,
          normalizeCell(row[column])
        ])
      )
    };
  });
  return {
    catalog_type: template.catalogType,
    sheet_name: sheetName,
    rows
  };
}

function normalizeCell(value: unknown): unknown {
  return typeof value === "string" ? value.trim() : value;
}

function isBlankRow(row: unknown[]): boolean {
  return row.every(isBlankCell);
}

function isBlankCell(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === "string" && !value.trim());
}

function cellText(value: unknown): string {
  return value === null || value === undefined ? "" : String(value).trim();
}
